import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { LanguageContext } from "../../../constant/LanguageContext";
import { apiFetch } from "../../../context/AuthContext";
import { getTrimmedCompanyContext } from "../utils/encodeUtils";
import { normalizeModuleKey } from "../utils/adminLevelUtils";
import { translations } from "../constants/translations";

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

export const PAGE_STATES = {
  HIDDEN: "HIDDEN",
  LOCKED: "VISIBLE_ACCESS_DENIED",
  FULL: "VISIBLE_ACCESSIBLE",
};

export const stateOfModule = (mp) =>
  mp?.state || (mp?.isEnabled ? PAGE_STATES.FULL : PAGE_STATES.HIDDEN);

export const parseJsonOrThrow = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  }
  return data;
};

/**
 * usePermissionLayers — the target-agnostic core of the 3-tier permission
 * editor. Owns the three layers and their INSTANT-SAVE mutators; the caller
 * decides WHICH admin level is being edited via `resolveLevelId`.
 *
 * Layers (kept strictly separate):
 *   1. pageStates    — pageKey -> HIDDEN | VISIBLE_ACCESS_DENIED | VISIBLE_ACCESSIBLE
 *   2. features      — opt-IN:  pageKey -> { featureKey: true } (absent = disabled)
 *   3. hiddenColumns — opt-OUT: pageKey -> { "table.column": true } (absent = visible)
 *
 * All mutations are optimistic: local state flips immediately, the API call
 * fires, and on failure the previous snapshot is restored + an error toast
 * is surfaced. There is no Save button.
 *
 * @param selectedCompany  {client, company} for the workingCompany payload
 * @param resolveLevelId   async () => levelId — may lazily create the level
 *                         (personal configs) or just return a fixed id
 *                         (shared levels). Called on every mutation.
 * @param hasTarget        boolean — mutations are no-ops until true, so a
 *                         stray click on an empty canvas never writes.
 */
export const usePermissionLayers = ({ selectedCompany, resolveLevelId, hasTarget }) => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const [pageStates, setPageStates] = useState({});
  const [features, setFeatures] = useState({});       // pageKey -> { featureKey: true }
  const [hiddenColumns, setHiddenColumns] = useState({}); // pageKey -> { "tbl.col": true }

  // moduleKey -> modulePermissionId (needed for feature/column endpoints)
  const modulePermissionIds = useRef({});

  // ── toast state (same auto-clear pattern as useAdminLevels) ──
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  useEffect(() => {
    if (success) { const id = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(id); }
  }, [success]);
  useEffect(() => {
    if (error) { const id = setTimeout(() => setError(null), 5000); return () => clearTimeout(id); }
  }, [error]);

  // ── ingest an adminLevel payload into the three layers ──
  const ingestLevel = useCallback((adminLevel) => {
    const nextStates = {};
    const nextFeatures = {};
    const nextHiddenCols = {};
    const idMap = {};

    (adminLevel?.modulePermissions || []).forEach((mp) => {
      const pageKey = normalizeModuleKey(mp.module);
      const state = stateOfModule(mp);
      // duplicate legacy spellings: most visible state wins
      const RANK = { HIDDEN: 0, VISIBLE_ACCESS_DENIED: 1, VISIBLE_ACCESSIBLE: 2 };
      if (nextStates[pageKey] === undefined || RANK[state] > RANK[nextStates[pageKey]]) {
        nextStates[pageKey] = state;
        idMap[pageKey] = mp.id;
      }
      (mp.featurePermissions || []).forEach((fp) => {
        const isColumn = fp.type === "column" || String(fp.featureName || "").includes(".");
        if (isColumn) {
          // columns are opt-out: only a row with isEnabled:false hides one
          if (fp.isEnabled === false) {
            if (!nextHiddenCols[pageKey]) nextHiddenCols[pageKey] = {};
            nextHiddenCols[pageKey][fp.featureName] = true;
          }
        } else if (fp.isEnabled) {
          // features are opt-in
          if (!nextFeatures[pageKey]) nextFeatures[pageKey] = {};
          nextFeatures[pageKey][fp.featureName] = true;
        }
      });
    });

    modulePermissionIds.current = idMap;
    setPageStates(nextStates);
    setFeatures(nextFeatures);
    setHiddenColumns(nextHiddenCols);
  }, []);

  // ── reset all layers (target cleared / switched) ──
  const resetLayers = useCallback(() => {
    setPageStates({});
    setFeatures({});
    setHiddenColumns({});
    modulePermissionIds.current = {};
  }, []);

  // absorb row ids from a freshly-created level without clobbering
  // optimistic layer state
  const absorbModuleIds = useCallback((adminLevel) => {
    (adminLevel?.modulePermissions || []).forEach((mp) => {
      const key = normalizeModuleKey(mp.module);
      if (!modulePermissionIds.current[key]) modulePermissionIds.current[key] = mp.id;
    });
  }, []);

  // ── low-level API helpers ──
  const postModule = useCallback(async (levelId, pageKey, state) => {
    const { company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    const res = await apiFetch(`${API_BASE_URL}/admin-levels/${levelId}/modules`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ module: pageKey, workingCompany: company, state }),
    });
    const data = await parseJsonOrThrow(res);
    if (data?.permission?.id) modulePermissionIds.current[pageKey] = data.permission.id;
    return data;
  }, [selectedCompany.client, selectedCompany.company]);

  // ensure the page has a modulePermission row; returns its id
  const ensureModulePermission = useCallback(async (pageKey, fallbackState) => {
    const existing = modulePermissionIds.current[pageKey];
    if (existing) return existing;
    const levelId = await resolveLevelId();
    await postModule(levelId, pageKey, fallbackState || PAGE_STATES.FULL);
    return modulePermissionIds.current[pageKey];
  }, [resolveLevelId, postModule]);

  // ── layer 1: page state (3-way) ──
  const setPageState = useCallback(async (pageKey, nextState) => {
    if (!hasTarget) return;
    const prevState = pageStates[pageKey];
    if (prevState === nextState) return;
    setPageStates((prev) => ({ ...prev, [pageKey]: nextState }));
    try {
      const levelId = await resolveLevelId();
      await postModule(levelId, pageKey, nextState);
      setSuccess(t.permissionSaved);
    } catch (err) {
      // roll back
      setPageStates((prev) => {
        const next = { ...prev };
        if (prevState === undefined) delete next[pageKey];
        else next[pageKey] = prevState;
        return next;
      });
      setError(t.permissionFailed);
    }
  }, [hasTarget, pageStates, resolveLevelId, postModule, t.permissionSaved, t.permissionFailed]);

  // ── layer 2: features (opt-in) ──
  const toggleFeature = useCallback(async (pageKey, featureKey, nextEnabled) => {
    if (!hasTarget) return;
    const prevPageFeatures = features[pageKey] || {};
    setFeatures((prev) => {
      const page = { ...(prev[pageKey] || {}) };
      if (nextEnabled) page[featureKey] = true;
      else delete page[featureKey];
      return { ...prev, [pageKey]: page };
    });
    try {
      const moduleId = await ensureModulePermission(pageKey, pageStates[pageKey]);
      if (nextEnabled) {
        const res = await apiFetch(`${API_BASE_URL}/admin-levels/modules/${moduleId}/features`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ featureName: featureKey, isEnabled: true, type: "feature" }),
        });
        await parseJsonOrThrow(res);
      } else {
        const res = await apiFetch(
          `${API_BASE_URL}/admin-levels/modules/${moduleId}/features/${encodeURIComponent(featureKey)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ featureName: featureKey, isEnabled: false, type: "feature" }),
          }
        );
        await parseJsonOrThrow(res);
      }
      setSuccess(t.permissionSaved);
    } catch (err) {
      setFeatures((prev) => ({ ...prev, [pageKey]: prevPageFeatures }));
      setError(t.permissionFailed);
    }
  }, [hasTarget, features, pageStates, ensureModulePermission, t.permissionSaved, t.permissionFailed]);

  // ── layer 3: table columns (opt-out, featureName = "table.column") ──
  const toggleColumn = useCallback(async (pageKey, tableKey, columnKey, nextVisible) => {
    if (!hasTarget) return;
    const featureName = `${tableKey}.${columnKey}`;
    const prevPageCols = hiddenColumns[pageKey] || {};
    setHiddenColumns((prev) => {
      const page = { ...(prev[pageKey] || {}) };
      if (nextVisible) delete page[featureName];
      else page[featureName] = true;
      return { ...prev, [pageKey]: page };
    });
    try {
      const moduleId = await ensureModulePermission(pageKey, pageStates[pageKey]);
      // POST upserts for both directions (isEnabled:false hides, true restores)
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/modules/${moduleId}/features`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featureName, isEnabled: !!nextVisible, type: "column" }),
      });
      await parseJsonOrThrow(res);
      setSuccess(t.permissionSaved);
    } catch (err) {
      setHiddenColumns((prev) => ({ ...prev, [pageKey]: prevPageCols }));
      setError(t.permissionFailed);
    }
  }, [hasTarget, hiddenColumns, pageStates, ensureModulePermission, t.permissionSaved, t.permissionFailed]);

  return {
    // layers (read)
    pageStates, features, hiddenColumns,
    // layers (write — instant save, optimistic)
    setPageState, toggleFeature, toggleColumn,
    // ingest / reset
    ingestLevel, resetLayers, absorbModuleIds,
    // toasts
    error, success, setError, setSuccess,
  };
};
